/**
 * @author Priya Murpani
 * @created 2025-11-11
 * @updated 2025-11-13
 */
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  debounceTime,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  of,
  Subject,
  Subscription,
  takeUntil
} from 'rxjs';
import { MyApplicationsService } from './my-applications.service';
import {
  ApplicationTableRow,
  MyApplicationsData
} from './my-applications.component.model';
import {
  ContractorService,
  DataService,
  DocumentService,
  WorkflowContextService
} from 'src/app/services';
import { UploadRow } from 'src/app/components/common/common-attachments/common-attachments.component';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  ItemSearchOptionField,
  ItemSearchOptionFieldOption,
  ItemSearchUtils
} from 'src/app/components/filter-list/models/filterClasses';
import {
  FilterBuilderFilter,
  FilterBuilderOutput,
  FilterBuilderParam,
  FilterBuilderParamOpts,
  translateFilterBuilderToLegacy
} from 'src/app/models/filter-builder';
import {
  WorkflowApplicationDocument,
  WorkflowApplicationVM
} from 'src/app/models';
import moment from 'moment';
import { WorkflowCurrentActivityComponent } from './workflow-current-activity/workflow-current-activity.component';
import { MatSort } from '@angular/material/sort';
import { ColumnOption } from 'src/app/models/grid-settings';
import { WorkflowJanusStatusPillComponent } from './my-applications-status-pill.component';
import { TableStateService } from './table-state-service';
import { svgs } from 'src/assets/export.assets';
// Added by amisha on 05/07/2027 to type shared-table columns explicitly.
// Reason: My Applications now uses shared keyboard-focus metadata and should keep compile-time safety for table config.
import { TableColumn } from 'src/app/components/common/common-table/common-table.component';
import { ApplicationRequiredDocumentsComponent } from 'src/app/components/workflow/application-required-documents/application-required-documents.component';

export interface SavedAttachmentRow {
  id: number;
  documentName: string;
  documentPath: string;
  uploaded: string;
  applicationId: string;
}

@Component({
  selector: 'wm-app-my-applications',
  standalone: false,
  templateUrl: './my-applications.component.html',
  styleUrls: ['./my-applications.component.scss'],
  // Added by Amisha on 05/25/2026.
  // Reason: allow page-scoped Material search overrides under .my-applications-root
  // without changing shared filter-builder styles or using ng-deep.
  encapsulation: ViewEncapsulation.None
})
export class MyApplicationsComponent implements OnInit {
  private sub!: Subscription;

  showChat = false;
  // Added by Amisha on 2026-09-09.
  // Reason: notify the shared chat to highlight when an applicant attempts to open a different application chat.
  chatAttentionTrigger = 0;
  selectedApplicationId = '';
  private shouldRefreshChatTableOnClose = false;
  unreadCount = 0;
  msgCount = 0;
  unreadCountOfAttachments = 0;
  colorOfAttachment = 'red';
  showErrorModal = false;
  // added by priya 04-02-2026
  clientId: string;
  searchText = '';
  errorFetchingDataTable: string = '';

  errorModalConfig = {
    open: true,
    modalDescp: 'Chat Error',
    modalSubDescp: 'Unable to send message. Please try again.',
    okBtn: true,
    yesNoBtn: false,
    closeBtn: false,
    status: 'error'
  };
  @Input() public workflowId: string;
  @Input() public sortField = 'dateCreated';

  @Input() public sortDescending = true;
  @Input() params: FilterBuilderParam[];
  @Input() defaultFilters: FilterBuilderFilter[];
  totalRecords = 0;
  public page = 1;
  public submitPage = 0;
  public pageSize = 10;
  public pageCount = 1;
  public recordCount = 0;
  @Input() public showAll = false;
  @Output() recordCountSet = new EventEmitter<number>();
  @Output() dataLoaded = new EventEmitter<string>();
  applications: WorkflowApplicationVM[];
  unpermissibleCount = 0;
  @Input() public filterCriteria: FilterBuilderParam[];
  public filterOptions: ItemSearchOptionField[];
  private requestTrigger$ = new Subject<void>();
  requestMapDataEvent: Subject<boolean> = new Subject<boolean>();
  @Input() staticFilters: ItemSearchOptionField[] = [];
  private destroy$ = new Subject<void>();
  private readonly SORT_CACHE_KEY = 'datatable-workflow-application-sort';
  private initialized = false;
  private readonly PAGE_SIZE_STORAGE_KEY = 'wm-common-table-page-size-applicant-applications-janus';
  private fetchSpecificDataBasedOnId = false;

  @ViewChild('appViewAnchor')
  appViewAnchor!: ElementRef<HTMLElement>;
  workflowOpts: FilterBuilderParamOpts[] = [];
  data: {
    applicationId: string;
    applicationNumber: string;
    permitType: string;
    description: string;
    applicantsName: string;
    dateCreated: string;
    status?: string;
    currentActivityDetails: {
      activityName: string;
      activityDataId: string;
    }[];
    chat?: string;
    attach?: string;
    applicantStatus: string;
    unreadCount?: number;
    msgCount?: number;
    totalDocuments?: number;
    unreadCountOfAttachments: number;
    departmentName: string;
  }[] = [];

  isSortOpen = false;
  selectedSortLabel: string = '';
  selectedSortOption: string | null = null;
  isSortOpenAbove = false;

  private triggerApi(): void {
    this.requestTrigger$.next();
  }
  SortByOptions = [
    'All',
    'Department',
    'Permit Type',
    'Most Recent',
    'Incomplete',
    'Submitted'
  ];
@HostListener('window:resize')
onWindowResize(): void {
  this.isMobile();
}

isMobile(): void {
  this.isMobileDevice = window.innerWidth < 768;
// Keep App View visible in the correct overlay mode when the screen is resized and when switching between mobile and desktop and vice versa
  const hasChildRoute = !!this.route.firstChild;

  if (hasChildRoute) {
    if (this.isMobileDevice) {
      this.isOverlayOpen = true;
      this.showOverlay = false;
    } else {
      this.showOverlay = true;
      this.isOverlayOpen = false;
    }
  }
}
// Added by Priya: Dynamically position the Sort By dropdown above or below
// the button based on the actual menu height and available viewport space.

toggleSort(event: MouseEvent): void {
  event.stopPropagation();

  if (this.isSortOpen) {
    this.isSortOpen = false;
    return;
  }

  this.isSortOpen = true;
  this.isSortOpenAbove = false;

  this.cdr.detectChanges();

  requestAnimationFrame(() => {
    const wrapper = this.sortWrapper?.nativeElement as HTMLElement;
    const button = wrapper?.querySelector('.sort-btn') as HTMLElement;
    const menu = wrapper?.querySelector('.sort-menu') as HTMLElement;

    if (!button || !menu) {
      return;
    }

    const buttonRect = button.getBoundingClientRect();
    const menuHeight = menu.getBoundingClientRect().height;

    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    this.isSortOpenAbove =
      spaceBelow < menuHeight && spaceAbove > spaceBelow;

    this.cdr.detectChanges();
  });
}
  selectSort(option: string) {
    this.selectedSortOption = option;
    this.selectedSortLabel = option;
    this.isSortOpen = false;

    switch (option) {
      case 'All':
        this.sortField = 'dateCreated';
        this.sortDescending = true;
        break;
      case 'Department':
        this.sortField = 'department';
        this.sortDescending = false;
        break;
      case 'Permit Type':
        this.sortField = 'permitType';
        this.sortDescending = false;
        break;
      case 'Most Recent':
        this.sortField = 'dateCreated';
        this.sortDescending = true;
        break;
      case 'Incomplete':
        this.sortField = 'incomplete';
        this.sortDescending = false;
        break;
      case 'Submitted':
        this.sortField = 'submitted';
        this.sortDescending = false;
        break;
    }
    this.submitPage = 0;
    this.triggerApi();
  }

  @ViewChild('sortWrapper') sortWrapper!: ElementRef;

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (
      this.isSortOpen &&
      this.sortWrapper &&
      !this.sortWrapper.nativeElement.contains(event.target)
    ) {
      this.isSortOpen = false;
    }
  }
  @Input() filterTypes = [
    {
      name: 'contains'
    },
    {
      name: 'is'
    },
    {
      name: 'in'
    },
    {
      name: 'range'
    }
  ];
  public filtersReady = false;
  private readonly COLUMN_CACHE_KEY =
    'datatable-workflow-application-list-pane';

  private readonly FILTER_CACHE_KEY = 'datatable-workflow-application-filters';

  isDestroyed: any;
  columnOptions: ColumnOption[] = [];
  tableType: 'Applicant' = 'Applicant';
  displayedColumns: string[] = [];
  @ViewChild(MatSort) sort!: MatSort;
  showFilters = false;
  // end by priya 04-02-2026
  loading: boolean = true;
  errorMessage: string = '';
  loadingDataForApplication: boolean = false;
  errorMessageOfData: string = '';
  @Input() showSimpleSearch = true;
  @Input() simpleSearchTitle = 'Search';
  currentAttachmentApplicationId!: string;
  savedAttachments: SavedAttachmentRow[] = [];
  private activeSummaryFilter?: string;

  //Started by Vipul Rathod on 19-12-2025
  assessorUseOnlyValue: string | null = null;
  simpleSearchValue: string;
  //Ended by Vipul Rathod on 19-12-2025

  constructor(
    private myApplicationsService: MyApplicationsService,
    private dataService: DataService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    public ref: ChangeDetectorRef,
    public _context: WorkflowContextService,
    private _contractorSvc: ContractorService,
    private _router: Router,
    private scrollService: TableStateService
  ) {}
  // added by priya 04-02-2026

  myWorkflowsFilter = [
    {
      title: 'submitterId',
      options: [
        {
          title: 'submitterId',
          selected: true,
          filterText: '',
          searchText: this._context.user ? this._context.user.id : null,
          strOperator: null
        }
      ]
    }
  ];
  private isApplicationViewOpen(url: string): boolean {
    return url.includes('/workflow-application/') ||
      url.includes('/child-application/') ||
      url.includes('/workflow-application-print-summary/');
  }
  async ngOnInit() {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);

    if (storedPageSize) {
      const pageSize = Number(storedPageSize);

      if ([10, 25, 100, 200].includes(pageSize)) {
        this.pageSize = pageSize;
      }
    }

    this.myApplicationsService.isInProgressOverlay = true;

    const cached = localStorage.getItem(this.FILTER_CACHE_KEY);

    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        this.filterOptions = parsed.filters ?? [];
      } catch {}
    }
    this.isLoading = true;
    this.loadingDataForApplication = false;

    this.isMobile();
    window.addEventListener('resize', () => this.isMobile());

    setTimeout(() => {
      const el = document.querySelector('.main-container') as HTMLElement;
      if (el) el.style.height = '';
    });

    const sortCache = localStorage.getItem(this.SORT_CACHE_KEY);
    if (sortCache) {
      try {
        const parsed = JSON.parse(sortCache);
        this.sortField = parsed.sortField ?? this.sortField;
        this.sortDescending = parsed.sortDescending ?? this.sortDescending;
      } catch (e) {
        console.error('Invalid sort cache JSON', e);
      }
    }

    this.initBaseFilterCriteria();
    this.staticFilters = [];
    await this.initFilters();

    this.loadColumnState();

    this.initDataSubscription();

    this.route.parent.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.clientId = params.get('clientId');

        if (this.clientId && this._context.client) {
          this._context.client.id = this.clientId;
        }
        if (!this.initialized) return;

        this.isLoading = this.data.length === 0;
        this.loadingDataForApplication = this.data.length !== 0;
        this.triggerApi();
      });

    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const pageParam = params.get('p');
        if (params.get('s') === 'external' ) {

          setTimeout(() => {
            this.appViewAnchor.nativeElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          });
        }
        if (pageParam) {
          this.submitPage = Math.max(0, Number(pageParam) - 1);
        } else {
          this.submitPage = 0;
        }
        if (!this.isFirstLoadHandled) {
          this.isFirstLoadHandled = true;
          this.isLoading = this.data.length === 0;
          this.loadingDataForApplication = this.data.length !== 0;
          // this used to call this.triggerApi() here, which caused a
          // duplicate GetAllWorkflowApplications on initial page load. The
          // <wm-filter-builder> child fires its own initial (filtersChange)
          // emission ~300ms after ngOnInit (via its unconditional
          // filtersChanged() call in ngOnInit → debounceTime(300) subject),
          // and our filtersChanged() handler already calls triggerApi() with
          // the correct submitPage (parsed above from the URL). Removing this
          // redundant trigger leaves exactly one initial request.
        }
      });

    this.sub = this.myApplicationsService.data$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        const newFilter = data?.summaryDetails?.activeSummaryFilter;

        if (this.activeSummaryFilter !== newFilter) {
          this.activeSummaryFilter = newFilter;
          if (!this.initialized) return;
          this.triggerApi();
        }

        this.myApplicationsService.refresh$
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.isLoading = this.data.length === 0;
            this.loadingDataForApplication = this.data.length !== 0;
            this.triggerApi();
          });
      });

    this.route.firstChild?.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const applicationId = params.get('applicationId');
       if (applicationId) {


          this.selectedApplicationId = applicationId;
// Set the device state on route load to ensure the App View overlay opens correctly after refresh.
  if (this.isMobileDevice) {
    this.isOverlayOpen = true;
    this.showOverlay = false;
  } else {
    this.showOverlay = true;
    this.isOverlayOpen = false;
  }
          const isExternal =
            this.route.snapshot.queryParamMap.get('s') === 'external';

          if (isExternal) {
            this.fetchSpecificDataBasedOnId = true;
            this.myApplicationsService.selectedApplicationId = applicationId;
            this.triggerApi();
          }
        }
      });

this._router.events
  .pipe(takeUntil(this.destroy$))
  .subscribe(() => {
    const hasChildRoute = !!this.route.firstChild;

    if (this.isMobileDevice) {
      this.isOverlayOpen = hasChildRoute;
      this.showOverlay = false;
    } else {
      this.showOverlay = hasChildRoute;
      this.isOverlayOpen = false;
    }

    setTimeout(() => {
      if (hasChildRoute) {
        this.disableSummaryInteractions();
      } else {
        this.enableSummaryInteractions();
      }
    });
  });

this.initialized = true; 
 }
  private enableSummaryInteractions(): void {
    const cards = document.querySelectorAll('.hover-summary-card');
    cards.forEach((el: any) => {
      el.style.pointerEvents = 'auto';
      el.style.cursor = 'pointer';
    });

    const footers = document.querySelectorAll(
      '.alert-summary-footer wm-common-button'
    );
    footers.forEach((el: any) => {
      el.style.setProperty('--button-font-color', '#2196F3', 'important');
      el.style.pointerEvents = 'auto';
      el.style.cursor = 'pointer';
    });
  }

  ngDoCheck() {
    document.body.style.overflow =
      this.showOverlay || this.isOverlayOpen ? 'hidden' : '';
  }

  private loadColumnState(): void {
    const cachedStr = localStorage.getItem(this.COLUMN_CACHE_KEY);

    const defaultColumns = [
      { name: 'applicationNumber', checked: true },
      { name: 'permitType', checked: true },
      { name: 'description', checked: false },
      { name: 'parcelAddress', checked: true },
      { name: 'parcelCity', checked: false },
      { name: 'parcelOwner', checked: false },
      { name: 'feeStatus', checked: true },
      { name: 'inspectionStatus', checked: true },
      { name: 'departmentName', checked: true },
      { name: 'dateCreated', checked: false },
      { name: 'applicantStatus', checked: true },
      { name: 'currentActivityName', checked: false },
      { name: 'chat', checked: true },
      { name: 'attach', checked: true }
    ];

    let finalColumns = defaultColumns;

    if (cachedStr) {
      try {
        const cached = JSON.parse(cachedStr);
        if (Array.isArray(cached.columnOptions)) {
          finalColumns = cached.columnOptions;
        }
      } catch (e) {
        console.error('Invalid cache');
      }
    }

    this.columnOptions = this.columns.map(col => {
      const found = finalColumns.find(c => c.name === col.key);
      return {
        name: col.key,
        label: col.label ?? col.key,
        checked: ['chat', 'attach'].includes(col.key)
          ? true
          : found
          ? found.checked
          : false,
        includeInExport: true
      };
    });

    this.updateDisplayedColumns();
  }
  @ViewChild('attachmentsPanel') attachmentsPanel!: ElementRef;
  showScrollTopBtn: boolean = false;
  private routeBodyElement: HTMLElement | null = null;

  ngAfterViewInit() {
      // Added by Priya on 12-10-2026: Get the route-body element to detect manual scrolling.

     this.routeBodyElement = document.querySelector('.route-body');
  // Added by Priya on 12-10-2026: Close the Sort By dropdown when the user manually scrolls
  // the route body without blocking the browser's default scrolling behavior.

  this.routeBodyElement?.addEventListener(
    'scroll',
    this.closeSortOnScroll,
    { passive: true }
  );
    if (this.sort && this.sortField) {
      this.sort.active = this.sortField;
      this.sort.direction = this.sortDescending ? 'desc' : 'asc';
    }

    this._router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (!this.scrollService.shouldRestoreScroll()) return;

        setTimeout(() => {
          requestAnimationFrame(() => {
            const scrollEl = document.querySelector(
              '.route-body'
            ) as HTMLElement;

            const position = this.scrollService.getScrollPosition();

            if (scrollEl) {
              scrollEl.scrollTo({
                top: position,
                behavior: 'auto'
              });
            }

            this.scrollService.resetRestoreFlag();
          });
        }, 50);
      });
    if (this.isMobile) {
      this.attachDynamicScrollListener();
    }
  }
  attachDynamicScrollListener() {
    setTimeout(() => {
      let scrollEl: HTMLElement | null = null;

      if (this.isAttachmentOpen) {
        scrollEl = document.querySelector('.attachment-content') as HTMLElement;
      } else if (this.isOverlayOpen && this.overlayContainer) {
        scrollEl = this.overlayContainer.nativeElement;
      } else {
        scrollEl =
          document.querySelector('.route-body') ||
          (document.querySelector('.main-body-container') as HTMLElement);
      }
      if (!scrollEl) return;

      scrollEl.addEventListener('scroll', () => {
        this.showScrollTopBtn = scrollEl!.scrollTop > 0;
        this.cdr.detectChanges();
      });
    });
  }
  private attachAttachmentScrollListener() {
    if (!ApplicationRequiredDocumentsComponent) return;

    const el = document.querySelector('.attachment-content') as HTMLElement;

    el?.removeEventListener('scroll', this.onAttachmentScroll);

    el?.addEventListener('scroll', this.onAttachmentScroll);
  }
  // Added by Priya: Close the Sort By dropdown when manual scrolling occurs
// within the route body to prevent the dropdown from remaining open
// at an outdated position.

private closeSortOnScroll = (): void => {
  if (this.isSortOpen) {
    this.isSortOpen = false;
  }
};

  private onAttachmentScroll = () => {
    if (!ApplicationRequiredDocumentsComponent) return;

    const el = document.querySelector('.attachment-content') as HTMLElement;

    this.showScrollTopBtn = el.scrollTop > 0;
  };
  private attachOverlayScrollListener() {
    if (!this.overlayContainer) return;

    const el = this.overlayContainer.nativeElement;

    el.removeEventListener('scroll', this.onOverlayScroll);

    el.addEventListener('scroll', this.onOverlayScroll);

    this.showScrollTopBtn = el.scrollTop > 0;
  }
  private onOverlayScroll = () => {
    if (!this.overlayContainer) return;

    const el = this.overlayContainer.nativeElement;

    this.showScrollTopBtn = el.scrollTop > 0;
  };

  onDisplayedColumnsChange(keys: string[]): void {
    this.displayedColumns = keys;

    this.columnOptions = this.columns.map(col => ({
      name: col.key,
      label: col.label ?? col.key,
      // checked: keys.includes(col.key),
      checked: ['chat', 'attach'].includes(col.key)
        ? true
        : keys.includes(col.key),
      includeInExport: true
    }));

    localStorage.setItem(
      this.COLUMN_CACHE_KEY,
      JSON.stringify({
        columnOptions: this.columnOptions
      })
    );
  }
  private updateDisplayedColumns(): void {
    this.displayedColumns = this.columnOptions
      .filter(col => col.checked)
      .map(col => col.name);
  }
  ngOnDestroy() {
    this.routeBodyElement?.removeEventListener(
    'scroll',
    this.closeSortOnScroll
  );
    this.destroy$.next();
    this.destroy$.complete();
    const el = document.querySelector('.main-container') as HTMLElement;
    if (el) {
      el.style.height = '100lvh';
    }
  }
  // end by priya 04-02-2026
  loadUnreadCountsForAllApplications(): void {
    const data: MyApplicationsData = this.myApplicationsService.getCurrentData();
    if (!data?.myApplicationsDetails?.applications) {
      return;
    }

    const apps = data.myApplicationsDetails.applications;
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const currentUserId = currentUser.id;

    apps.forEach((row: ApplicationTableRow) => {
      this.dataService
        .getWorkflowApplicationMessages(row.applicationId)
        .subscribe({
          next: res => {
            const unread = res.messages.filter(
              (msg: any) =>
                msg.hasBeenRead === false && msg.sentById !== currentUserId
            ).length;
            row.unreadCount = unread;

            const updated: MyApplicationsData = {
              ...data,
              myApplicationsDetails: {
                ...data.myApplicationsDetails,
                applications: [...apps]
              }
            };
            this.myApplicationsService.setData(updated);
          },
          error: () => {
            row.unreadCount = 0;
          }
        });
    });
  }

  openChat(row: any): void {
    if (!row) return;

    // Added by Amisha on 2026-09-09.
    // Reason: preserve the active chat and highlight it only when the requested application is different.
    if (this.showChat) {
      if (this.selectedApplicationId !== (row.applicationId ?? '')) {
        this.chatAttentionTrigger += 1;
      }
      return;
    }

    this.showChat = true;

    this.selectedApplicationId = row.applicationId ?? '';

    this.data = this.data.map(item =>
      item.applicationId === row.applicationId
        ? { ...item, unreadCount: 0 }
        : item
    );

    this.cdr.detectChanges();
  }

  handleChatMessageSent(event: { applicationId: string }): void {
    if (!event?.applicationId) {
      return;
    }

    this.data = this.data.map(item =>
      item.applicationId === event.applicationId
        ? {
            ...item,
            unreadCount: 0,
            msgCount: (item.msgCount ?? 0) + 1
          }
        : item
    );
    this.shouldRefreshChatTableOnClose = true;
  }

  async handleChatClosed(): Promise<void> {
    this.showChat = false;
    // Added by Amisha on 2026-09-09.
    // Reason: ensure a newly opened chat does not inherit the previous chat's attention request.
    this.chatAttentionTrigger = 0;
    this.selectedApplicationId = '';

    const shouldRefreshTable = this.shouldRefreshChatTableOnClose;
      this.shouldRefreshChatTableOnClose = false;

    try {
      // Added by amisha on 2026-08-26.
      // Reason: clear the chat deep-link before refreshing the table so stale
      // query parameters cannot reopen a chat the applicant already closed.
      await this._router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          tab: null,
          applicationId: null
        },
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    } finally {
      if (shouldRefreshTable) {
      this.triggerApi();
      }
    }
  }

  openErrorModal() {
    this.showErrorModal = true;
  }

  onActionClick(action: string): void {
    alert(`${action} clicked!`);
  }

  //Started by Vipul Rathod on 19-12-2025
  assessorUseOnly = [
    { label: 'Not Applicable', value: 'notApplicable' },
    { label: 'Not Approved', value: 'notApproved' },
    { label: 'Incomplete', value: 'incomplete' },
    { label: 'Approved', value: 'approved' },
    { label: 'Approved With Condition', value: 'approvedWithCondition' }
  ];

  //End by Vipul Rathod on 19-12-2025

  // added by priya 04-02-2026
  showOverlay = false;
  openApplication(applicationId: string): void {
    if (!applicationId) {
      return;
    }
    const scrollEl = document.querySelector('.route-body');

    const scrollPos = scrollEl?.scrollTop || 0;

    this.scrollService.setScrollPosition(scrollPos);
    if (!this.isMobileDevice) {
      this.showOverlay = true;
    }
    this._router
      .navigate(['workflow-application', applicationId], {
        relativeTo: this.route,
        queryParamsHandling: 'merge'
      })
      .then(() => {
        requestAnimationFrame(() => {
          const scrollEl = document.querySelector('.route-body') as HTMLElement;

          this.ensureMinimumScrollForFewRows(scrollEl);

          this.scrollToTableTop();
        });
      });
    setTimeout(() => {
      this.disableSummaryInteractions();
    }, 0);
  }
  openCurrentActivity(event: {
    applicationId: string;
    activityDataId?: string;
  }): void {
      // Prevent activity dropdown from opening while App View is loading
  WorkflowCurrentActivityComponent.setAppViewOpening(true);
  WorkflowCurrentActivityComponent.closeActiveDropdown();
    
    if (!event?.applicationId) {
      console.error('Invalid activity event', event);
      return;
    }

    const scrollEl = document.querySelector('.route-body');

    const scrollPos = scrollEl?.scrollTop || 0;

    this.scrollService.setScrollPosition(scrollPos);

    if (!this.isMobileDevice) {
      this.showOverlay = true;
    }
    this._router
      .navigate(
        ['workflow-application', event.applicationId, event.activityDataId],
        { relativeTo: this.route, queryParamsHandling: 'merge' }
      )
      .then(() => {
          WorkflowCurrentActivityComponent.setAppViewOpening(false);

        requestAnimationFrame(() => {
          const scrollEl = document.querySelector('.route-body') as HTMLElement;

          this.ensureMinimumScrollForFewRows(scrollEl);

          this.scrollToTableTop();
        });
      });
    setTimeout(() => {
      this.disableSummaryInteractions();
    }, 0);
  }
  private ensureMinimumScrollForFewRows(scrollEl: HTMLElement) {
    if (!scrollEl || !this.data) return;

    const element = this.myAppContainer?.nativeElement;
    if (!element) return;

    const elementTop = element.offsetTop;

    if (this.data.length <= 4) {
      scrollEl.scrollTo({
        top: 600,
        behavior: 'auto'
      });
      return;
    }

    const offset = 100;

    const safeScrollTop = Math.min(600, elementTop - offset);

    scrollEl.scrollTo({
      top: safeScrollTop,
      behavior: 'smooth'
    });
  }
  scrollToAbove() {
    this.showScrollTopBtn = true;
    if (this.isAttachmentOpen && this.attachmentComponent) {
      this.attachmentComponent.scrollToTop();
      return;
    }

    if (this.isOverlayOpen && this.overlayContainer) {
      this.overlayContainer.nativeElement.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }

    const scrollEl =
      document.querySelector('.route-body') ||
      document.querySelector('main-body-container');

    if (scrollEl) {
      const scrollPos = scrollEl.scrollTop || 0;
      this.scrollService.setScrollPosition(scrollPos);

      scrollEl.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }
  openApplicationSummary(event: { applicationId: string }): void {
    if (!event?.applicationId) return;
    const scrollEl = document.querySelector('.route-body');

    const scrollPos = scrollEl?.scrollTop || 0;

    this.scrollService.setScrollPosition(scrollPos);

    if (!this.isMobileDevice) {
      this.showOverlay = true;
    }
    this._router
      .navigate(
        [
          // 'application',
          'workflow-application-print-summary',
          event.applicationId
        ],
        { relativeTo: this.route, queryParamsHandling: 'merge' }
      )
      .then(() => {
        requestAnimationFrame(() => {
          const scrollEl = document.querySelector('.route-body') as HTMLElement;

          this.ensureMinimumScrollForFewRows(scrollEl);

          this.scrollToTableTop();
        });
      });
    setTimeout(() => {
      this.disableSummaryInteractions();
    }, 0);
  }

  private disableSummaryInteractions(): void {
    const cards = document.querySelectorAll('.hover-summary-card');
    cards.forEach((el: any) => {
      el.style.pointerEvents = 'none';
    });
    const footers = document.querySelectorAll(
      '.alert-summary-footer wm-common-button'
    );
    footers.forEach((el: any) => {
      el.style.setProperty(
        '--button-font-color',
        'rgba(153,153,153,0.4)',
        'important'
      );
      el.style.pointerEvents = 'none';
    });
  }

  @ViewChild('overlayContainer') overlayContainer!: ElementRef;
  @ViewChild('myAppContainer') myAppContainer!: ElementRef;
  @ViewChild(ApplicationRequiredDocumentsComponent)
  attachmentComponent!: ApplicationRequiredDocumentsComponent;
  scrollToTop() {
    this.myAppContainer?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  scrollToTableTop(): void {
    const scrollEl = document.querySelector('.main-body-container');
    if (scrollEl && this.myAppContainer) {
      const containerTop = scrollEl.getBoundingClientRect().top;
      const targetTop = this.myAppContainer.nativeElement.getBoundingClientRect()
        .top;

      const scrollOffset = targetTop - containerTop + scrollEl.scrollTop;

      scrollEl.scrollTo({
        top: scrollOffset,
        behavior: 'auto'
      });
    }
  }
  // Added by amisha on 05/07/2027 to preserve shared-table typing for keyboard-focusable row behavior.
  // Reason: the page-specific table configuration now relies on shared TableColumn metadata and helper callbacks.
  columns: TableColumn[] = [
    {
      key: 'applicationNumber',
      label: 'App #',
      headerType: 'text',
      // hidden: true,
      cellType: 'component',
      component: WorkflowCurrentActivityComponent,
      componentInputs: row => ({
        row,
        mode: 'applicationNumber'
      }),
      componentOutputs: {
        openSummary: (event: any) => this.openApplicationSummary(event),
        openApplication: (applicationId: string) =>
          this.openApplication(applicationId)
      }
    },
    {
      key: 'permitType',
      label: 'App Type',
      headerType: 'text',
      cellType: 'text'
    },
    // Start code added by Sanchit on 26-03-2026
    {
      key: 'description',
      label: 'Description',
      headerType: 'text',
      cellType: 'text'
      // hidden: true,
    },
    // {
    //   key: 'applicantsName',
    //   label: 'Applicant',
    //   headerType: 'text',
    //   cellType: 'text'
    // },
    {
      key: 'dateCreated',
      label: 'Date Created',
      // hidden: true,
      headerType: 'text',
      cellType: 'text',
      hideable: true
    },
    {
      key: 'parcelAddress',
      label: 'Site Address',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'parcelCity',
      label: 'City',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'parcelOwner',
      label: 'Owner',
      // hidden: true,
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'feeStatus',
      label: 'Fees',
      headerType: 'text',
      cellType: 'text'
    },
    {
      key: 'inspectionStatus',
      label: 'Inspection',
      headerType: 'text',

      // cellType: 'text'
      cellType: 'component',
      component: WorkflowJanusStatusPillComponent,
      componentInputs: row => ({
        row: {
          inspectionStatus: row.inspectionStatus
        }
      })
    },
    {
      key: 'departmentName',
      label: 'Department',
      headerType: 'text',
      // hidden: true,
      cellType: 'text'
    },
    // {
    //   key: 'issuedOn',
    //   label: 'Issue Date',
    //   hidden: true,
    //   headerType: 'text',
    //   cellType: 'text'
    // },
    // End code added by Sanchit on 26-03-2026

    {
      key: 'applicantStatus',
      label: 'Status',
      headerType: 'text',
      cellType: 'component',
      hideMenu: false, // code added by Sanchit on 26-03-2026
      hideable: true,
      component: WorkflowJanusStatusPillComponent,
      componentInputs: row => ({ row })
    },
    {
      key: 'currentActivityName',
      label: 'Current Activity',
      // hidden:true,
      headerType: 'text',
      cellType: 'component',
      component: WorkflowCurrentActivityComponent,
      componentInputs: row => ({
        row,
        mode: 'currentActivity'
      }),

      componentOutputs: {
        openApplication: (applicationId: string) =>
          this.openApplication(applicationId),
        openActivity: (event: any) => this.openCurrentActivity(event)
      }
    },
    {
      key: 'chat',
      label: 'Chat',
      hideable: false, // code added by Sanchit on 26-03-2026
      headerType: 'badge',
      icon: 'chat',
      cellType: 'icon',
      badgeField: 'unreadCount',
      // code edited by Krutika
      // iconColor: (row: any) => this.getChatColor(row)
      iconColor: (row: any) => this.getChatColor(row)
    },
    {
      key: 'attach',
      label: 'Attach',
      hideable: false, // code added by Sanchit on 26-03-2026
      headerType: 'badge',
      icon: 'attach_file',
      cellType: 'icon',
      badgeField: 'unreadCountOfAttachments',
      // code edited by Krutika

      // iconColor: (row: any) => this.getAttachmentColor(row)
      iconColor: (row: any) => this.getAttachmentColor(row)
    }
  ];

  // this used to be a getter that recomputed `this.columns.filter(...).map(...)`
  // on every read, returning a new array reference each time. Because it is bound in the
  // template as [focusableCellKeys]="focusableMyApplicationsColumnKeys" on <wm-common-table>,
  // Angular treated every Zone tick as an "input changed" and fired common-table.ngOnChanges,
  // which unconditionally schedules a setTimeout in updateSkeletonRows(). That setTimeout
  // completing kept Zone alive for another tick, and the loop starved the JS thread so XHR
  // callbacks (e.g. GetAllWorkflowApplications) could never fire. columns is a class field
  // that never changes, so materialize this once and reuse the same array reference.
  readonly focusableMyApplicationsColumnKeys: string[] = this.columns
    .filter(col => col.keyboardFocusable)
    .map(col => col.key);

  // Added by amisha on 05/07/2027 to make only real application rows keyboard-focusable.
  // Reason: row-level focus should be available only when the row can open the My Applications overlay.
  isMyApplicationsRowKeyboardFocusable = (row: any): boolean =>
    !!row?.applicationId;
  // end by priya 04-02-2026

  @ViewChild('openAttachmentModal', { static: false }) openAttachmentModal: any;
  @Input() open = false;
  @Output() close = new EventEmitter<undefined>();

  onAttachmentSubmitted(appId: string) {
    const row = this.data.find(d => d.applicationId === appId);

    if (row) {
      row.unreadCountOfAttachments = 0;
    }

    this.data = [...this.data];

    this.triggerApi();
  }
  isInProgress = false;
  isOverlayOpen = false;
  isPrintSummaryOverlay = false;

  openDetails(row: any) {
    if (!row?.applicationId) return;
    this.myApplicationsService.selectedApplicationId = row.applicationId;

    let activity: any = null;

    if (Array.isArray(row?.currentActivityDetails)) {
      activity = row.currentActivityDetails.at(-1);
    } else if (typeof row?.currentActivityDetails === 'string') {
      try {
        const parsed = JSON.parse(row.currentActivityDetails);
        activity = parsed?.at(-1);
      } catch {}
    }

    const status = (row.applicantStatus || '').toLowerCase().trim();

    this.isInProgress =
      activity?.activityDataId &&
      /^(in progress|incomplete|action needed|n\/a)$/i.test(status?.trim());

    if (this.isMobileDevice) {
      // ✅ FIRST: decide route
      this.isPrintSummaryOverlay = !this.isInProgress;

      const route = this.isInProgress
        ? this._router.navigate([
            '/jurisdiction',
            this.clientId,
            'applicant-home',
            'my-applications',
            'workflow-application',
            row.applicationId
          ])
        : this._router.navigate([
            '/jurisdiction',
            this.clientId,
            'applicant-home',
            'my-applications',
            'workflow-application-print-summary',
            row.applicationId
          ]);

      this.isOverlayOpen = true;

      this.showScrollTopBtn = false;

      route.then(() => {
        setTimeout(() => {
          if (this.isPrintSummaryOverlay) {
            this.attachOverlayScrollListener();

            if (this.overlayContainer) {
              const el = this.overlayContainer.nativeElement;
              this.showScrollTopBtn = el.scrollTop > 0;
            }
          } else {
            this.showScrollTopBtn = false;
          }
        }, 50);
      });
    }

    // DESKTOP
    if (this.isInProgress) {
      this.openApplication(row.applicationId);
    } else {
      this.openApplicationSummary({
        applicationId: row.applicationId
      });
    }
  }
  isMobileDevice = false;
  isAttachmentOpen = false;
  attachmentDisplayedColumns: string[] = [
    'thumbnail',
    'attachment',
    'uploaded',
    'action'
  ];
  handleClose() {
    this.showScrollTopBtn = false;
    if (ApplicationRequiredDocumentsComponent) {
      const el = document.querySelector('.attachment-content') as HTMLElement;

      el.removeEventListener('scroll', this.onAttachmentScroll);
    }

    this.isAttachmentOpen = false;
    this._router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        tab: null,
        applicationId: null
      },
      queryParamsHandling: 'merge'
    });

    const el1 = document.querySelector('.main-body-container') as HTMLElement;
    const el = document.querySelector('.main-container') as HTMLElement;
    if (el) {
      el.style.overflow = 'auto';
    }
    if (el1) {
      el1.style.overflowY = 'auto';
      const el2 = document.querySelector(
        '.header-parent-container'
      ) as HTMLElement;

      if (el2) {
        el2.style.display = 'flex';
      }
      setTimeout(() => {
        if (!el1) return;

        const scrollTop = el1.scrollTop;

        this.showScrollTopBtn = scrollTop > 0;
      }, 50);
    }
  }

  private setQueryParams(tab: string, applicationId: string) {
    this._router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        tab,
        applicationId
      },
      queryParamsHandling: 'merge'
    });
  }
  handleIconClick(event: { icon: string; row: any }) {
    if (event.icon === 'chat') {
      // Added by Amisha on 2026-09-09.
      // Reason: avoid changing the deep-link or chat context until the currently open chat is closed.
      if (this.showChat) {
        if (this.selectedApplicationId !== (event.row.applicationId ?? '')) {
          this.chatAttentionTrigger += 1;
        }
        return;
      }

      this.setQueryParams('chat', event.row.applicationId);
      this.openChat(event.row);
    }

    if (event.icon === 'attach_file') {
      if (!this.isAttachmentOpen) {
        this.setQueryParams('attachments', event.row.applicationId);
      }
      this.openAttachments(event.row.applicationId, event.row);
    }
  }
  getAttachmentColor(row: any): string {
    if (row?.unreadCountOfAttachments > 0) {
      return '#B3261E';
    }

    if (row?.totalDocuments > 0 && row?.unreadCountOfAttachments === 0) {
      return '#2196F3';
    }

    return '#5F6368';
  }
  ngOnChanges() {
    document.body.style.overflow = this.showOverlay ? 'hidden' : '';
  }
  get isNoData() {
    return !this.data || this.data.length === 0;
  }
  get hasFewData() {
    return this.data && this.data.length <= 4;
  }

  openAttachments(appId: string, row?: any) {
    this.showScrollTopBtn = false;

    const el = document.querySelector('.main-container') as HTMLElement;
    const el1 = document.querySelector('.main-body-container') as HTMLElement;

    const el2 = document.querySelector(
      '.header-parent-container'
    ) as HTMLElement;

    if (el) {
      if (this.isMobileDevice) {
        el.style.overflow = 'hidden';
      }
      el.style.height = '100lvh';
    }
    if (el1) {
      el1.style.overflowY = 'hidden';
    }
    if (el2) {
      if (this.isMobileDevice) {
        el2.style.display = 'none';
      }
      el2.style.zIndex = '1';
    }

    this.currentAttachmentApplicationId = appId;
    this.isAttachmentOpen = true;

    if (!row) {
      row = this.data.find(d => d.applicationId === appId);
      if (!row) {
        return;
      }
    }
    this.isAttachmentOpen = true;

    this.data = this.data.map(d =>
      d.applicationId === appId
        ? {
            ...d,
            unreadCountOfAttachments: 0
          }
        : d
    );
    if (row?.totalDocuments > 0 && row?.unreadCountOfAttachments > 0) {
      this.dataService
        .markApplicationDocumentsAsReadbyApplicant(appId)

        .subscribe();
    }
    setTimeout(() => {
      this.attachAttachmentScrollListener();
    }, 0);
  }

  uploadTableData: UploadRow[] = [];
  attachmentsByApplication: Record<string, UploadRow[]> = {};
  savedAttachmentsByApplication: Record<string, SavedAttachmentRow[]> = {};
  private isFirstLoadHandled = false;
  isLoading = false;
  arrowUpwardIcon = svgs.arrowUpwardIcon.name;
  private initDataSubscription(): void {
    this.requestTrigger$
      .pipe(
        debounceTime(200),
        filter(() => !!this._context.user),
        exhaustMap(() => {
          if (!this._context.user) return EMPTY;

          if (this.data.length === 0) {
            this.isLoading = true;
            this.loadingDataForApplication = false;
          } else {
            this.isLoading = false;
            this.loadingDataForApplication = true;
          }
          this.cdr.detectChanges();
          let filter = ItemSearchUtils.mergeFilters(
            this.filterOptions,
            this.myWorkflowsFilter
          );

          if (this.activeSummaryFilter) {
            filter = [
              ...filter,
              {
                title: this.activeSummaryFilter,
                options: [
                  new ItemSearchOptionFieldOption({
                    filterText: this.activeSummaryFilter
                  })
                ]
              }
            ];
          }
          if (this.fetchSpecificDataBasedOnId && this.myApplicationsService.selectedApplicationId) {
            filter = [
              ...filter,
              {
                title: 'applicationId',
                options: [
                  new ItemSearchOptionFieldOption({
                    title: 'applicationId',
                    filterText: this.myApplicationsService
                      .selectedApplicationId,
                    strOperator: 'is',
                    searchText: null,
                    selected: true
                  })
                ]
              }
            ];
          }

          const finalSortField = this.sortField || 'dateCreated';
          const finalSortDescending = this.sortDescending ?? true;
          return this.dataService
            .getAllWorkflowApplications(
              this.workflowId,
              finalSortField,
              finalSortDescending,
              filter,
              this.submitPage + 1,
              this.pageSize,
              null,
              this.showAll
            )
            .pipe(
              takeUntil(this.destroy$),

              finalize(() => {
                this.isLoading = false;
                this.loadingDataForApplication = false;
                this.cdr.detectChanges();
              })
            );
        })
      )
      .subscribe({
        next: applications => {
          this.handleResponse(applications);
        },
        error: err => {
          this.isLoading = false;
          this.loadingDataForApplication = false;
          this.errorMessage =
            err.message || 'Failed to load the My Apps data table';
        }
      });
  }
  private handleResponse(applications: any): void {
    this.errorMessageOfData = '';

    this.buildPermitTypeOptions(applications.workflowApplications);

    if (!applications.workflowApplications?.length) {
      this.errorMessageOfData = 'No Data Found';
      this.data = [];
      return;
    }

    this.page = this.submitPage;
    this.pageCount = applications.pageCount;
    this.recordCount = applications.recordCount;
    this.totalRecords = applications?.recordCount || 0;
    this.data = applications.workflowApplications.map(wa => ({
      applicationId: wa.appId,
      applicationNumber: wa.applicationNumber,
      permitType: wa.permitType,
      description: wa.description,
      applicantsName: wa.applicantsName,
      parcelAddress: wa.parcelAddress,
      parcelOwner: wa.parcelOwner,
      parcelCity: wa.parcelCity,
      feeStatus: wa.feeStatus,
      inspectionStatus: this.formatInspectionStatus(wa.inspectionStatus),
      departmentName: wa.departmentName,
      issuedOn: wa.issuedOn ? moment(wa.issuedOn).format('MM/DD/YYYY') : '',
      dateCreated: moment(wa.dateCreated).format('MM/DD/YYYY'),
      applicantStatus: wa.applicantStatus,
      applicationSubmittedOn: wa.applicationSubmittedOn,
      status: wa.status,

      currentActivityDetails: this.parseActivity(wa.currentActivityData),
      currentActivityName: this.getActivityName(wa.currentActivityData),
      chat: 'chat',
      attach: 'attach_file',
      unreadCount: wa.unreadCount || 0,
      msgCount: wa.msgCount || 0,
      totalDocuments: wa.documentCount,
      unreadCountOfAttachments: wa.unreadDocumentCount
    }));
    if (this.isApplicationViewOpen(this._router.url)) {
      setTimeout(() => {
        this.disableSummaryInteractions();
      });
    }
    const tab = this.route.snapshot.queryParamMap.get('tab');
    const applicationId = this.route.snapshot.queryParamMap.get(
      'applicationId'
    );
    if (tab === 'chat' && applicationId) {
      const row = this.data.find(d => d.applicationId === applicationId);
      if (row) this.openChat(row);
    }
    if (tab === 'attachments' && applicationId && !this.isAttachmentOpen) {
      const row = this.data.find(d => d.applicationId === applicationId);

      if (row) {
        this.openAttachments(applicationId, row);
      }
    }
  }
  private parseActivity(data: any) {
    try {
      return Array.isArray(data) ? data : JSON.parse(data || '[]');
    } catch {
      return [];
    }
  }

  private getActivityName(data: any): string {
    const parsed = this.parseActivity(data);
    return parsed.length ? parsed.at(-1).activityName : '';
  }
  // Added by amisha on 11-03-2026 to set chat icon color based on unread messages
  getChatColor(row: any): string {
    if (row?.unreadCount > 0) {
      return '#B3261E';
    }

    if (row?.msgCount > 0) {
      return '#2196F3';
    }

    return '#5F6368';
  }

  // Start code added by Sanchit on 26-03-2026
  private formatInspectionStatus(status: string | null | undefined): string {
    const value = status
      ?.toString()
      .trim()
      .toLowerCase();

    switch (value) {
      case 'complete':
        return 'Success';

      case 'needed':
        return 'Action Required';

      case 'na':
      case 'n/a':
      case null:
      case undefined:
      case '':
        return 'N/A';

      default:
        return status || '-';
    }
  }
  // End code added by Sanchit on 26-03-2026
  emitDataLoaded() {
    this.dataLoaded.emit('WorkflowApplicationListComponent');
  }
  private buildPermitTypeOptions(applications: any[]) {
    const map = new Map<string, any>();

    applications.forEach(app => {
      const type = app.permitType;
      if (type && !map.has(type)) {
        map.set(type, { name: type, value: type });
      }
    });

    const newOpts = Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    if (!this.workflowOpts?.length && newOpts.length) {
      this.workflowOpts = newOpts;

      this.filtersReady = false;
      this.initBaseFilterCriteria();

      setTimeout(() => {
        this.filtersReady = true;
        this.cdr.detectChanges();
      });
    }
  }
  onSortChanged(event: {
    sortField: string | null;
    sortDescending: boolean | null;
  }): void {
    if (!event.sortField) return;

    this.sortField = event.sortField;
    this.sortDescending = event.sortDescending ?? true;

    localStorage.setItem(
      this.SORT_CACHE_KEY,
      JSON.stringify({
        sortField: this.sortField,
        sortDescending: this.sortDescending
      })
    );

    this.submitPage = 0;
    this.triggerApi();
  }
  initFilters(): Promise<void> {
    const workflows$ = this._context.client
      ? this.dataService.getWorkflows(this._context.client, false)
      : this.dataService.getPublicWorkflows(null, false);

    const contractorWorkflows$ = this._context.client
      ? this._contractorSvc.getContractorRegistrationTypes(
          this._context.client.id
        )
      : of([]);

    return Promise.all([
      workflows$.toPromise(),
      contractorWorkflows$.toPromise()
    ]).then(([workflows, contractorWorkflows]) => {
      if (workflows?.length) {
        this.workflowOpts = [
          ...workflows.map(wf => ({
            name: wf.version?.name ?? wf.name,
            value: wf.id
          })),
          ...contractorWorkflows.map(c => ({
            name: `${c.name} (Registration)`,
            value: c.registerWorkflowId
          })),
          ...contractorWorkflows.map(c => ({
            name: `${c.name} (Renewal)`,
            value: c.renewWorkflowId
          }))
        ].sort((a, b) => a.name.localeCompare(b.name));
      } else {
        this.workflowOpts = [];
      }

      this.filterCriteria = [];
      this.initBaseFilterCriteria();

      this.filtersReady = true;
    });
  }
  private initBaseFilterCriteria(): void {
    this.filterCriteria = [
      {
        name: 'App Type',
        id: 'permitType',
        types: ['is', 'in'],
        options: [...this.workflowOpts]
      },
      { name: 'Applicant Name', id: 'applicantsName' },
      {
        name: 'Application Number',
        id: 'applicationNumber'
      },
      {
        name: 'Current Activity',
        id: 'currentActivity',
        types: ['is', 'contains', 'in']
      },
      {
        name: 'Date Created',
        id: 'dateCreated',
        inputType: 'date',
        types: ['is', 'range']
      },
      { name: 'Description', id: 'description' },
      { name: 'Parcel Address', id: 'parcelAddress' },
      {
        name: 'Parcel City',
        id: 'parcelCity',
        types: ['is', 'contains', 'in']
      },
      { name: 'Parcel ID', id: 'parcelId', types: ['is', 'contains', 'in'] },
      { name: 'Parcel Owner', id: 'parcelOwner', types: ['is', 'contains'] },
      {
        name: 'Status',
        id: 'applicantStatus',
        types: ['is', 'in'],
        options: [
          { name: 'In Progress', value: 'In Progress' },
          { name: 'Completed', value: 'Complete' },
          { name: 'Incompleted', value: 'Incompleted' },

          { name: 'Voided', value: 'Voided' },

          { name: 'Action Needed', value: 'Action Needed' }
        ]
      },

      { name: 'Submitter', id: 'submitterId', types: ['is'] }
    ];
  }
  private filterInitialized = false;

  filtersChanged(filters: FilterBuilderOutput) {
    if (!this.filtersReady) return;

    let legacyFilters: ItemSearchOptionField[] = translateFilterBuilderToLegacy(
      filters
    );
    const simpleSearchOption = legacyFilters
      .flatMap(f => f.options ?? [])
      .find(o => o.searchOptionMethod === 'simpleSearch' && o.filterText);

    const simpleSearchValue = simpleSearchOption?.filterText?.trim();

    if (this.showSimpleSearch && simpleSearchValue) {
      const appNumberFilter = legacyFilters.find(
        f => f.title === 'applicationNumber'
      );
      const permitTypeFilter = legacyFilters.find(
        f => f.title === 'permitType'
      );

      if (appNumberFilter) {
        appNumberFilter.options = [
          new ItemSearchOptionFieldOption({
            selected: true,
            filterText: simpleSearchValue,
            searchText: '',
            strOperator: '',
            searchOptionMethod: 'simpleSearch'
          })
        ];
      } else {
        legacyFilters.push(
          new ItemSearchOptionField({
            title: 'applicationNumber',
            options: [
              new ItemSearchOptionFieldOption({
                selected: true,
                filterText: simpleSearchValue,
                searchText: '',
                strOperator: '',
                searchOptionMethod: 'simpleSearch'
              })
            ]
          })
        );
      }
      if (permitTypeFilter) {
        permitTypeFilter.options = [
          new ItemSearchOptionFieldOption({
            selected: true,
            filterText: simpleSearchValue,
            searchText: '',
            strOperator: '',
            searchOptionMethod: 'simpleSearch'
          })
        ];
      } else {
        legacyFilters.push(
          new ItemSearchOptionField({
            title: 'permitType',
            options: [
              new ItemSearchOptionFieldOption({
                selected: true,
                filterText: simpleSearchValue,
                searchText: '',
                strOperator: '',
                searchOptionMethod: 'simpleSearch'
              })
            ]
          })
        );
      }
    }

    this.filterOptions = [...legacyFilters];

    localStorage.setItem(
      this.FILTER_CACHE_KEY,
      JSON.stringify({
        filters: this.filterOptions,
        savedAt: Date.now()
      })
    );
    this.isLoading = this.data.length === 0;
    this.loadingDataForApplication = this.data.length !== 0;
    if (this.filterInitialized) {
      this.submitPage = 0;
    } else {
      this.filterInitialized = true;
    }
        this.triggerApi();
  }

  @ViewChild('commonTable') commonTable!: any;

  openGlobalColumnMenu() {
    this.commonTable?.openColumnMenu();
  }
  onPageChange(page: number): void {
    this.submitPage = page;
    this._router.navigate([], {
      relativeTo: this.route,
      queryParams: { p: page + 1 },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });

    this.scrollToTop();
    this.triggerApi();
  }
  onRowsPerPageChange(size: number): void {
    const pageSize = Number(size) || 10;
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));

    this.pageSize = pageSize;
    this.triggerApi();
  }

  onRetry(): void {
    this.data = [];
    this.isLoading = true;
    this.loadingDataForApplication = false;

    this.triggerApi();
  }

  // end by priya 04-03-2026
}
